import { ref, watch } from 'vue';

import { defineStore } from 'pinia';

import Api from '@/api/Api';

import MapConsts from '@/components/map/consts/MapConsts';
import { FEATURE_TYPE, MAP_MODE } from '@/components/map/consts/mapFeatureConsts';
import { useMapFeaturesStore } from '@/stores/mapFeaturesStore';
import { useSubNetworksStore } from '@/stores/subNetworksStore';


// type VisibilityObjectContents
const storeDetails = (visibilityObject, item, minValue, maxValue, multiplier, precision) => {
	visibilityObject[item.gnss] = {
		valueBright: item.avg_val * multiplier,
		valuePale: null,
		currentDatetimeBright: item.current_datetime,
		currentDatetimePale: null,
		valueIsOld: item.value_is_old,
		minValue: minValue,
		maxValue: maxValue,
		precision: precision,
	};
};


const storeBase = (name, apiRequest, minValue, maxValue, multiplier, precision) => defineStore(name, () => {
	const dbItems = ref(new Map());
	// Каждый элемент соответствует контракту GnssTooltipDisplayItem
	// (см. @/stores/types/mapStoreContracts.ts), который читает useMapData.
	// TODO(TS_MIGRATION): типизировать через GnssTooltipDisplayItem[] после перевода стора на TS.
	const displayItems = ref([]);
	const nonNullGnss = ref([]);
	const isVisible = ref(false);
	// snr/multipath/residuals не используют gridpoint-видимость; контракт MapModeStoreContract
	// (см. @/stores/types/mapStoreContracts.ts) требует это поле — отдаём всегда true.
	// TODO(TS_MIGRATION): убрать заглушку после перевода стора на TS.
	const needsVisibilityData = ref(true);

	const featuresStore = useMapFeaturesStore();

	const getDisplayItems = () => {
		if (!isVisible.value) {
			return [];
		}
		const displayItemsResult = featuresStore.features.filter(item => item.featureType === FEATURE_TYPE.baseStation).map(feature => {
			const mountpoint = feature.featureData.mntpnt_int;
			return {
				...feature,
				stats: dbItems.value.has(mountpoint) ? dbItems.value.get(mountpoint) : null,
			};
		});
		return displayItemsResult;
	};

	const updateDisplayItems = () => {
		displayItems.value = getDisplayItems();
	}

	watch(() => featuresStore.features, updateDisplayItems);

	const updateDbItems = dbItemsValue => {
		let sortedItems = new Map();
		let showGnss = [];
		for (let item of dbItemsValue) {
			if (!sortedItems.has(item.mountpoint)) {
				sortedItems.set(item.mountpoint, {});
			}
			storeDetails(sortedItems.get(item.mountpoint), item, minValue, maxValue, multiplier, precision);
			showGnss.push(item.gnss);
		}
		dbItems.value = sortedItems;
		nonNullGnss.value = [...new Set(showGnss)];
		updateDisplayItems();
	}

	const handleGet = responseData => {
		updateDbItems(responseData.items);
	};
	const handleGetError = error => {
		if (error.response.status !== 401) {
			Api.logError(error);
		}
	};
	const subNetworksStore = useSubNetworksStore();
	const refresh = () => {
		if (isVisible.value) {
			// parameter subNetworkLabel is used only for residuals
			apiRequest(handleGet, handleGetError, subNetworksStore.subNetworkSelectedId);
		}
	};
	watch(
		() => subNetworksStore.subNetworkSelectedId,
		() => {
			refresh();
		}
	);

	const setVisibility = visibility => {
		isVisible.value = visibility;
		updateDisplayItems();
		refresh();
	};

	return {
		dbItems,
		displayItems,
		nonNullGnss,
		isVisible,
		needsVisibilityData,
		refresh,
		setVisibility,
	};
});


export const useSnrStore = storeBase('snrStore', Api.snrGet, MapConsts.SNR_MIN, MapConsts.SNR_MAX, 1, MapConsts.SNR_PRECISION);
export const useMultipathStore = storeBase('multipathStore', Api.multipathGet, MapConsts.MULTIPATH_MIN, MapConsts.MULTIPATH_MAX, 1, MapConsts.MULTIPATH_PRECISION);
// m -> mm
export const useResidualsStore = storeBase('residualsStore', Api.residualsGet, MapConsts.RESIDUALS_MIN, MapConsts.RESIDUALS_MAX, 1000, MapConsts.RESIDUALS_PRECISION);

export const factory = key => {
	switch (key) {
	case MAP_MODE.snr:
		return useSnrStore();
	case MAP_MODE.multipath:
		return useMultipathStore();
	case MAP_MODE.residuals:
		return useResidualsStore();
	default:
		return null;
	}
}